import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, Renderer2, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AppComponent } from '../../../app.component';
import { CommonModule } from '@angular/common';
import { DatePicker } from 'primeng/datepicker';
import { DateRangeValidator } from '../../validators/date-range.validator';
import { SsnDirective } from '../../directives/ssn.directive';
import { ZipDirective } from '../../directives/zip.directive';
import { FaxDirective } from '../../directives/fax.directive';

interface JsonFormValidators {
  min?: number;
  max?: number;
  required?: boolean;
  requiredTrue?: boolean;
  email?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  nullValidator?: boolean;
  ssn?:boolean;
}
interface JsonFormControlOptions {
  min?: string;
  max?: string;
  step?: string;
  icon?: string;
  showCount?: boolean;
  count?: number;
}
interface JsonFormControls {
  name: string;
  label: string;
  value: any;
  type: string;
  placeholder: string;
  helperText?: string;
  options?: JsonFormControlOptions | any;
  required: boolean;
  validators: JsonFormValidators;
  readonly?: boolean;
  helpText?:string;
}
export interface JsonFormData {
  controls: JsonFormControls[];
}

@Component({
  selector: 'app-json-form',
  imports: [FormsModule, ReactiveFormsModule, CommonModule, DatePicker, SsnDirective, ZipDirective, FaxDirective],
  templateUrl: './json-form.component.html',
  styleUrl: './json-form.component.css',
  standalone:true
})
export class JsonFormComponent implements OnInit, OnChanges {
  @Input()
  formData: FormGroup | any;

  @Input()
  controlsData: JsonFormControls[] = [];

  @Output()
  valueChangeEvent = new EventEmitter<any>();

  @Output() InitializationCompletedEvent = new EventEmitter<any>();
  emailFormat: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  isInvalidEmail: boolean = false;

  @Input()
  isQuePage: boolean = false;

  @Input()
  controlWidth: boolean = false;

  buildConfig2: any;
  IsFroalaEditor: boolean = false;

  @Input()
  textAreaCol: number = 30;

  @Input()
  textAreaRows: number = 3;

  @Input()
  customClass: string = "";

  @Input()
  textAreaCounterPositionClass: string = 'rightFloat';

  zipCodeRegexFormat: RegExp = /^\d{5}([-]|\s*)?(\d{4})?$/;

  faxRegexFormat:RegExp = /^(?:\d{10}|\d{3}-\d{3}-\d{4})$/;
  isInvalidFax:boolean = false;

  ssnRegexFormat:RegExp = /^(?:\d{9}|\d{3}-\d{2}-\d{4})$/;

  // Date range validation properties
  startDateControl: JsonFormControls | null = null;
  endDateControl: JsonFormControls | null = null;

  constructor(
    private fb: FormBuilder,
    private elementRef: ElementRef,
    private appComponent: AppComponent,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.createForm(this.controlsData);
    var tempFilterData = this.controlsData;
    tempFilterData.forEach((field) => {
      if (field.type === 'date') {
        if (field.options.min) {
          field.options.min = new Date(field.options.min);
        }
        if (field.options.max) {
          field.options.max = new Date(field.options.max);
        }
        if (field.value) {
          field.value = new Date(field.value);
        }
        
        // Identify start and end date controls for validation
        if (field.name === 'incidentStartDt') {
          this.startDateControl = field;
        } else if (field.name === 'incidentEndDt') {
          this.endDateControl = field;
        }
      }
    });
    this.InitializationCompletedEvent.emit();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['controlsData'] && !changes['controlsData'].firstChange) {
      this.createForm(this.controlsData);
      // Setup date picker behavior after form is recreated
      setTimeout(() => {
        this.setupDatePickerBehavior();
      }, 100);
    }
  }

  onDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    if (value.length >= 3 && value.length <= 4) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    } else if (value.length >= 5) {
      value =
        value.substring(0, 2) +
        '/' +
        value.substring(2, 4) +
        '/' +
        value.substring(4);
    }
    input.value = value;
  }
  createForm(controls: JsonFormControls[]) {
    for (const control of controls) {
      const validatorsToAdd = [];
      
      if (control.validators?.required && !control.required) {
        control.required = control.validators.required;
      } else if (control.required && !control.validators?.required) {
        if (!control.validators) control.validators = {};
        control.validators.required = control.required;
      }
      
      for (const [key, value] of Object.entries(control.validators)) {
        switch (key) {
          case 'min':
            validatorsToAdd.push(Validators.min(value));
            break;
          case 'max':
            validatorsToAdd.push(Validators.max(value));
            break;
          case 'required':
            if (value) {
              validatorsToAdd.push(Validators.required);
            }
            break;
          case 'requiredTrue':
            if (value) {
              validatorsToAdd.push(Validators.requiredTrue);
            }
            break;
          case 'email':
            if (value) {
              validatorsToAdd.push(Validators.email);
            }
            break;
          case 'regex':
            if (value) {
              validatorsToAdd.push(Validators.pattern(value));
            }
            break;
          case 'minLength':
            validatorsToAdd.push(Validators.minLength(value));
            break;
          case 'maxLength':
            validatorsToAdd.push(Validators.maxLength(value));
            break;
          case 'pattern':
            validatorsToAdd.push(Validators.pattern(value));
            break;
          case 'fax':
              validatorsToAdd.push(Validators.pattern(this.faxRegexFormat));
             break;
          case 'nullValidator':
            if (value) {
              validatorsToAdd.push(Validators.nullValidator);
            }
            break;
          default:
            break;
        }
      }
      
      if (control.type === 'ssn') {
        validatorsToAdd.push(Validators.pattern(/^(?:\d{9}|\d{3}-\d{2}-\d{4})$/));
      }
      if (control.type === 'zip') {
        validatorsToAdd.push(Validators.pattern(/^(?:\d{5}|\d{9}|\d{5}-\d{4})$/));
      }
      if (control.type === 'fax') {
        validatorsToAdd.push(Validators.pattern(/^(\d{10}|\d{3}-\d{3}-\d{4}|\(\d{3}\) \d{3}-\d{4})$/));
      }
      if (control.type === 'email') {
        validatorsToAdd.push(Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/));
      }

      const initialValue = control.value !== null ? control.value : "";
      const formControl = this.fb.control(initialValue, validatorsToAdd);
      
      this.formData?.addControl(control.name, formControl);

      if (control.value && control.value.length > 0 && control.type == 'fax') {
        if (!this.faxRegexFormat.test(control.value)) {
          this.isInvalidFax = true;
        }
      }
    }
    this.markRequiredEmptyFieldsAsTouched();
  }

  private markRequiredEmptyFieldsAsTouched() {
    for (const control of this.controlsData) {
      if (control.validators?.required && (!control.value || control.value === "")) {
        const formControl = this.formData?.get(control.name);
        if (formControl) {
          formControl.markAsTouched();
          formControl.updateValueAndValidity();
        }
      }
    }
  }

  ngAfterViewInit() {
    const uiElement = this.elementRef.nativeElement.querySelectorAll(
      '.p-datepicker-trigger'
    );
    uiElement.forEach((element: any) => {
      element.setAttribute('aria-label', 'Toggle Datepicker');
      element.setAttribute('title', 'Datepicker');
      element.setAttribute('role', 'button');
    });

    const paragraphs =
      this.elementRef.nativeElement.querySelectorAll('.dynRequired');
    paragraphs.forEach((span: HTMLElement) => {
      this.renderer.setStyle(span, 'font-size', '14px');
      this.renderer.setStyle(span, 'font-weight', 'normal');
    });

    const img = this.elementRef.nativeElement.querySelectorAll('img');
    img.forEach((p: HTMLElement) => {
      p.setAttribute('alt', 'img');
    });

    // Setup date picker input click behavior
    this.setupDatePickerBehavior();
  }

  private setupDatePickerBehavior() {
    setTimeout(() => {
      const datePickers = this.elementRef.nativeElement.querySelectorAll('.dateFieldOnlineReferrals .p-datepicker');
      
      datePickers.forEach((datePicker: HTMLElement) => {
        const input = datePicker.querySelector('input') as HTMLInputElement;
        const trigger = datePicker.querySelector('.p-datepicker-trigger') as HTMLElement;
        
        if (input && trigger) {
          // Remove existing event listeners by cloning the input
          const newInput = input.cloneNode(true) as HTMLInputElement;
          input.parentNode?.replaceChild(newInput, input);
          
          // Add custom click behavior to input - only allow manual entry
          newInput.addEventListener('click', (event) => {
            event.stopPropagation();
            event.preventDefault();
            newInput.focus();
            // Ensure calendar doesn't open
            const calendar = datePicker.querySelector('.p-datepicker-panel');
            if (calendar) {
              calendar.classList.add('p-datepicker-panel-hidden');
            }
          });

          // Prevent focus from opening calendar
          newInput.addEventListener('focus', (event) => {
            event.stopPropagation();
          });

          // Allow manual typing
          newInput.addEventListener('keydown', (event) => {
            // Allow normal typing behavior
            event.stopPropagation();
          });
        }
      });
    }, 500);
  }

  onCalendarClick() {
    const calendarPrev =
      this.elementRef.nativeElement.querySelector('.p-datepicker-prev');
    const calendarNext =
      this.elementRef.nativeElement.querySelector('.p-datepicker-next');

    if (calendarPrev) {
      calendarPrev.setAttribute('aria-label', 'prevButton');
    }
    if (calendarNext) {
      calendarNext.setAttribute('aria-label', 'nextButton');
    }
  }

  formatDateToMMDDYYYY(value: any, item: any) {
    if (item.value) {
      var value = item.value.toISOString().split('T')[0];
      value.toString();
      this.modelChangeEvent(value, item);
    }
  }

  modelChangeEvent(value: any, item: any) {
    const data = {
      value: value,
      name: item.name,
    };
    if (item.type == 'textarea') {
      item.options.count = item.validators.maxLength - value.length;
    }
    if (item.type == 'email') {
      this.onKeyUpEmailFormatEvent(value,item);
    }
    if(item.type == 'fax'){
      this.onKeyUpFaxEvent(value,item);
    }
    if (item.type == 'date') {
      this.validateDateRange(item);
    }
    this.valueChangeEvent.emit(data);
  }

  onKeyUpEmailFormatEvent(event: any, item: any) {
    const data = {
      value: event,
      name: item.name,
    };
    if (event) {
      event.srcElement.value && !this.emailFormat.test(event.srcElement.value)
        ? (this.isInvalidEmail = true)
        : (this.isInvalidEmail = false);
    }
  }

  onKeyUpFaxEvent(event:any,item:any){
    const data = {
      value: event,
      name: item.name,
    };
    if (event) {
      (event.srcElement.value && !this.faxRegexFormat.test(event.srcElement.value)) 
      ? this.isInvalidFax = true 
      : this.isInvalidFax = false;
    }
  }

  validateDateRange(item: JsonFormControls) {
    // Only validate if we have both start and end date controls
    if (!this.startDateControl || !this.endDateControl) {
      return;
    }

    const startDate = this.startDateControl.value;
    const endDate = this.endDateControl.value;

    // If either date is empty, don't validate
    if (!startDate || !endDate) {
      // Clear any existing date range errors
      const endDateFormControl = this.formData?.get('incidentEndDt');
      if (endDateFormControl && endDateFormControl.errors && endDateFormControl.errors['dateRangeInvalid']) {
        delete endDateFormControl.errors['dateRangeInvalid'];
        if (Object.keys(endDateFormControl.errors).length === 0) {
          endDateFormControl.setErrors(null);
        }
      }
      return;
    }

    // Convert to Date objects if they're not already
    const startDateObj = startDate instanceof Date ? startDate : new Date(startDate);
    const endDateObj = endDate instanceof Date ? endDate : new Date(endDate);

    // Validate that both dates are valid
    if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
      return;
    }

    // Check if end date is before start date
    if (endDateObj < startDateObj) {
      // Mark the end date control as invalid
      const endDateFormControl = this.formData?.get('incidentEndDt');
      if (endDateFormControl) {
        endDateFormControl.setErrors({ 
          ...endDateFormControl.errors, 
          dateRangeInvalid: { message: 'System should not allow Incident End date to be before the Incident Start date' }
        });
      }
    } else {
      // Clear the error if dates are valid
      const endDateFormControl = this.formData?.get('incidentEndDt');
      if (endDateFormControl && endDateFormControl.errors) {
        delete endDateFormControl.errors['dateRangeInvalid'];
        if (Object.keys(endDateFormControl.errors).length === 0) {
          endDateFormControl.setErrors(null);
        }
      }
    }
  }
}